import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, CircleHelp, Lightbulb, Sparkles, TriangleAlert } from 'lucide-react';
import {
  getTariffAssistantFaq,
  getTariffAssistantState,
  getTariffAssistantSuggestions,
  getTariffAssistantWarnings,
} from '@/modules/tarifas/helpers/tariffAssistantRules.js';
import { buttonClass, shellCard } from '@/pages/Tarifas/components/shared.jsx';

const MOBILE_QUERY = '(max-width: 1023px)';

function getInitialOpen(storageKey) {
  if (typeof window === 'undefined') return true;

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
  } catch {
    return true;
  }

  return !window.matchMedia(MOBILE_QUERY).matches;
}

function ChecklistItem({ item }) {
  return (
    <li className="flex items-start gap-2 text-sm text-[rgba(var(--fg),0.84)]">
      <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${item.done ? 'text-[rgb(var(--brand))]' : 'text-[rgba(var(--muted),0.6)]'}`} />
      <span className={item.done ? '' : 'text-[rgba(var(--muted),0.95)]'}>{item.label}</span>
    </li>
  );
}

export default function TariffAssistantPanel({
  formState,
  storageKey = 'tariff-assistant',
  className = '',
}) {
  const [open, setOpen] = useState(() => getInitialOpen(storageKey));
  const state = useMemo(() => getTariffAssistantState(formState), [formState]);
  const warnings = useMemo(() => getTariffAssistantWarnings(formState), [formState]);
  const suggestions = useMemo(() => getTariffAssistantSuggestions(formState), [formState]);
  const faqItems = useMemo(() => getTariffAssistantFaq(formState), [formState]);
  const [selectedFaqId, setSelectedFaqId] = useState(() => faqItems[0]?.id || '');

  useEffect(() => {
    setOpen(getInitialOpen(storageKey));
  }, [storageKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, open ? 'open' : 'closed');
    } catch {
      // La asistencia es contextual y local; si localStorage falla, el panel igual sigue operativo.
    }
  }, [open, storageKey]);

  useEffect(() => {
    if (!faqItems.some((item) => item.id === selectedFaqId)) {
      setSelectedFaqId(faqItems[0]?.id || '');
    }
  }, [faqItems, selectedFaqId]);

  const activeFaq = faqItems.find((item) => item.id === selectedFaqId) || faqItems[0] || null;

  return (
    <aside className={`xl:sticky xl:top-4 xl:self-start ${className}`}>
      <section className={`${shellCard} overflow-hidden`}>
        <button
          type="button"
          className="flex w-full items-start justify-between gap-3 px-4 py-4 text-left"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-[rgba(var(--brand),0.2)] bg-[rgba(var(--brand),0.08)] px-2.5 py-1 text-[11px] font-semibold text-[rgb(var(--brand))]">
                {state.badge}
              </span>
              <span className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.16)] px-2.5 py-1 text-[11px] font-semibold text-[rgba(var(--fg),0.84)]">
                {state.statusLabel}
              </span>
            </div>
            <h3 className="mt-3 text-base font-semibold text-[rgb(var(--fg))]">Asistente de carga</h3>
            <p className="mt-1 text-sm text-[rgba(var(--muted),0.95)]">Guía contextual para completar la tarifa sin errores.</p>
          </div>
          <span className="mt-1 rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] p-2 text-[rgba(var(--fg),0.82)]">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </button>

        {open ? (
          <div className="space-y-4 border-t border-[rgba(var(--border),0.24)] px-4 py-4">
            <section className="rounded-2xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--subpanel),0.16)] p-4">
              <div className="flex items-start gap-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--brand))]" />
                <div>
                  <p className="text-sm font-semibold text-[rgb(var(--fg))]">Estado actual</p>
                  <p className="mt-1 text-sm leading-6 text-[rgba(var(--fg),0.84)]">{state.summary}</p>
                  <p className="mt-2 text-xs text-[rgba(var(--muted),0.95)]">{state.progressLabel}</p>
                </div>
              </div>
            </section>

            <section>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">Checklist</p>
              <ul className="mt-3 space-y-2">
                {state.checklist.map((item) => <ChecklistItem key={item.id} item={item} />)}
              </ul>
            </section>

            {warnings.length ? (
              <section className="rounded-2xl border border-[rgba(var(--danger),0.18)] bg-[rgba(var(--danger),0.06)] p-4">
                <div className="flex items-center gap-2">
                  <TriangleAlert className="h-4 w-4 text-[rgb(var(--danger))]" />
                  <p className="text-sm font-semibold text-[rgb(var(--fg))]">Alertas suaves</p>
                </div>
                <ul className="mt-3 space-y-2 text-sm text-[rgba(var(--fg),0.84)]">
                  {warnings.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </section>
            ) : null}

            {suggestions.length ? (
              <section>
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-[rgb(var(--brand))]" />
                  <p className="text-sm font-semibold text-[rgb(var(--fg))]">Sugerencias</p>
                </div>
                <div className="mt-3 space-y-2">
                  {suggestions.map((item) => (
                    <div key={item} className="rounded-2xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--panel),0.45)] px-3 py-3 text-sm text-[rgba(var(--fg),0.84)]">
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {state.nextSteps.length ? (
              <section>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">Próximos pasos</p>
                <ul className="mt-3 space-y-2 text-sm text-[rgba(var(--fg),0.84)]">
                  {state.nextSteps.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </section>
            ) : null}

            <section>
              <div className="flex items-center gap-2">
                <CircleHelp className="h-4 w-4 text-[rgb(var(--brand))]" />
                <p className="text-sm font-semibold text-[rgb(var(--fg))]">Dudas frecuentes</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {faqItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={selectedFaqId === item.id ? `${buttonClass} border-[rgba(var(--brand),0.3)] bg-[rgba(var(--brand),0.08)] text-[rgb(var(--brand))]` : buttonClass}
                    onClick={() => setSelectedFaqId(item.id)}
                  >
                    {item.question}
                  </button>
                ))}
              </div>
              {activeFaq ? (
                <div className="mt-3 rounded-2xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--subpanel),0.16)] p-4 text-sm leading-6 text-[rgba(var(--fg),0.84)]">
                  {activeFaq.answer}
                </div>
              ) : null}
            </section>
          </div>
        ) : null}
      </section>
    </aside>
  );
}
